// Typed URL search params for the production routes (Module 04 §4 S1/S4).
//
// §4 S1's filters live in the URL so a filtered list survives a reload and a
// share, and so a saved view can name them: a view stores the list's filters
// under these same keys, and its order as the `sort` token (spec 16 §3.12
// items 1 and 2, D-305 rulings 3 and 5). `view` names the active saved view.
// The three chips that were URL presets here until PR-E4 are now system saved
// views served by `GET /v1/saved-views` (spec 04 §4 S1, D-305 ruling 11), so
// this file holds no preset of its own. TanStack Router only types a
// `<Link search=…>` when the target route declares `validateSearch`, so the
// shapes live here (imported by router.tsx for the route definitions and by
// the screens for their state).
//
// Validation is defensive by design (the inventory house pattern): an unknown
// or malformed param is dropped, never thrown on. A shared link with a stale
// filter must still open the screen, and a default is an absent key, so a bare
// address stays byte-identical.
//
// S1's address holds to the saved-view grammar (R7, PR-E4 wave 4), because a
// view stores these filters as they stand and its contract refuses what the
// list route would take (packages/contracts/src/saved-views/screens.ts):
// `facility_id`, `work_center_id` and `product_id` are uuids, `due_before` and
// `due_after` are calendar dates that exist, and `q` is at most
// `VIEW_SEARCH_TEXT_MAX` characters with no NUL, the cap the search box's
// `maxLength` shares. Anything else is dropped here, so it never reaches the
// wire or a saved view, and a stored filter this validator drops reads as
// stale when a view is applied.

import { VIEW_SEARCH_TEXT_MAX, viewIdParam } from "../../views/lib/screens";

const WO_STATUSES = [
  "draft",
  "released",
  "in_progress",
  "completed",
  "closed",
  "cancelled",
] as const;
type WoStatusParam = (typeof WO_STATUSES)[number];

const SORTS = ["due_date", "priority", "created_at"] as const;
type SortParam = (typeof SORTS)[number];

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** A uuid, loosely: the shape the platform mints (v7) without pinning the
 * version, which is what the contract's `z.guid()` accepts. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/u;

function uuidParam(value: unknown): string | undefined {
  return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
}

/** A calendar date `YYYY-MM-DD` that exists, the value a date input writes
 * and the saved-view contract's `CalendarDate` accepts. */
function calendarDate(value: unknown): string | undefined {
  if (typeof value !== "string" || !CALENDAR_DATE_RE.test(value)) return undefined;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const probe = new Date(0);
  probe.setUTCFullYear(year, month - 1, day);
  return probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
    ? value
    : undefined;
}

/** The search box's text: 1 to `VIEW_SEARCH_TEXT_MAX` characters with no NUL
 * character, which Postgres text cannot hold. */
function searchText(value: unknown): string | undefined {
  const text = str(value);
  if (text === undefined || text.length > VIEW_SEARCH_TEXT_MAX) return undefined;
  return text.includes("\u0000") ? undefined : text;
}

function isWoStatus(value: unknown): value is WoStatusParam {
  return typeof value === "string" && (WO_STATUSES as readonly string[]).includes(value);
}

/**
 * `?status=` accepts one value or a list of distinct known statuses, the list
 * the saved-view contract stores (W8, PR-E4 wave 5). A list holding a value
 * the contract refuses, or one status twice, is refused whole rather than
 * trimmed, so the address never narrows to a subset nobody chose, and a view
 * that stored such a list applies without it and says its filter no longer
 * applies (spec 16 §3.12 item 7).
 */
function statusList(value: unknown): readonly WoStatusParam[] | undefined {
  const raw: readonly unknown[] = Array.isArray(value) ? value : [value];
  if (raw.length === 0 || !raw.every(isWoStatus)) return undefined;
  return new Set(raw).size === raw.length ? raw : undefined;
}

export interface WorkOrdersSearch {
  /**
   * The active saved view's id (spec 16 §3.12 item 5). Not a filter: the view
   * layer reads it, and an id that names no view the reader can see is
   * removed from the address with the notice "This view no longer exists".
   */
  readonly view?: string | undefined;
  readonly status?: readonly WoStatusParam[] | undefined;
  readonly facility_id?: string | undefined;
  readonly work_center_id?: string | undefined;
  readonly product_id?: string | undefined;
  readonly due_before?: string | undefined;
  readonly due_after?: string | undefined;
  readonly overdue?: true | undefined;
  readonly q?: string | undefined;
  readonly sort?: SortParam | undefined;
  /**
   * THE PEG REQUEST (D-290): the sales order detail's "Plan a work order" link
   * lands here with the order and the line it is for, and the list opens the
   * create modal PRE-PEGGED to that line (§4 S3). Not filters: neither narrows
   * the list, and both leave the URL when the modal closes. Only honoured as a
   * PAIR — a line id means nothing without the order it is read from. A saved
   * view never touches them: applying a view, un-pressing its chip and All all
   * keep the pair (D-305 ruling 18).
   */
  readonly peg_order_id?: string | undefined;
  readonly peg_line_id?: string | undefined;
}

export function validateWorkOrdersSearch(search: Record<string, unknown>): WorkOrdersSearch {
  const pegOrderId = str(search["peg_order_id"]);
  const pegLineId = str(search["peg_line_id"]);
  const pegged = pegOrderId !== undefined && pegLineId !== undefined;
  return {
    // A view id or nothing, the one rule every adopting screen reads `view`
    // by: a malformed value reads as absent, with no notice (W8).
    view: viewIdParam(search["view"]),
    status: statusList(search["status"]),
    facility_id: uuidParam(search["facility_id"]),
    work_center_id: uuidParam(search["work_center_id"]),
    product_id: uuidParam(search["product_id"]),
    due_before: calendarDate(search["due_before"]),
    due_after: calendarDate(search["due_after"]),
    overdue: search["overdue"] === true || search["overdue"] === "true" ? true : undefined,
    q: searchText(search["q"]),
    sort:
      typeof search["sort"] === "string" && (SORTS as readonly string[]).includes(search["sort"])
        ? (search["sort"] as SortParam)
        : undefined,
    // Half a pair is dropped, not half-honoured (the defensive rule above).
    peg_order_id: pegged ? pegOrderId : undefined,
    peg_line_id: pegged ? pegLineId : undefined,
  };
}

export interface DispatchSearch {
  readonly facility_id?: string | undefined;
  readonly department_id?: string | undefined;
}

/** S4's facility → department selection, kept in the URL so the board a
 * planner set up survives a reload and can be pinned as a bookmark. */
export function validateDispatchSearch(search: Record<string, unknown>): DispatchSearch {
  return {
    facility_id: str(search["facility_id"]),
    department_id: str(search["department_id"]),
  };
}

export interface BlockedSearch {
  readonly facility_id?: string | undefined;
}

/**
 * S13's facility narrowing.
 *
 * ABSENT IS A REAL ANSWER here, not an unset default: with no facility the
 * board reads every plant's open downtime in one call (§5 e22's `facility_id`
 * is optional), which is what makes "which of my stations is down" answerable
 * with zero picks. Picking one narrows e22 and, more importantly, BOUNDS the
 * station-queue fan-out the quality-hold half needs — so the param is the
 * screen's scope control, and it belongs in the URL for the same reason S4's
 * does: a supervisor pins the plant they run.
 */
export function validateBlockedSearch(search: Record<string, unknown>): BlockedSearch {
  return { facility_id: str(search["facility_id"]) };
}

const PIECE_RATE_STATUSES = ["captured", "priced", "settled"] as const;
type PieceRateStatusParam = (typeof PIECE_RATE_STATUSES)[number];

export interface PieceRateSearch {
  readonly facility_id?: string | undefined;
  readonly shift_date_from?: string | undefined;
  readonly shift_date_to?: string | undefined;
  readonly status?: PieceRateStatusParam | undefined;
}

/**
 * S14's filters (§5 e27's own query set, minus `worker_id` — see below).
 *
 * THE PERIOD IS THE POINT, so it lives in the URL: a piece-rate ledger is read
 * for a payroll period, and a period a supervisor had to re-pick after every
 * reload is a period they will get wrong once. `shift_date_*` are SHIFT dates
 * (§9.7 — the day the shift STARTED), which the screen says beside the pickers
 * rather than in a tooltip.
 *
 * NO DEFAULT RANGE, deliberately. Every other date default in this area is
 * computed in the FACILITY's calendar (D-089), and this screen legitimately
 * spans facilities — there is no one calendar to default in. An invented
 * "last 7 days" would also hide older entries behind a filter the reader never
 * chose, on the one screen whose job is to show what was captured.
 *
 * `worker_id` is NOT a URL param: e27 honours it only for a caller holding
 * `production.view_costs` and pins everyone else to their own rows whatever it
 * says, so a shareable link carrying one would mean different things to two
 * readers. Narrowing to one worker is successor work behind a real person
 * picker (see the §4 row 14 note).
 */
export function validatePieceRateSearch(search: Record<string, unknown>): PieceRateSearch {
  const status = search["status"];
  return {
    facility_id: str(search["facility_id"]),
    shift_date_from: str(search["shift_date_from"]),
    shift_date_to: str(search["shift_date_to"]),
    status:
      typeof status === "string" && (PIECE_RATE_STATUSES as readonly string[]).includes(status)
        ? (status as PieceRateStatusParam)
        : undefined,
  };
}
